import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { DatePickerTabs } from "src/app/core/constants/constants";
import { Helper } from "src/app/core/helpers/helper";
import { UsersService } from "../services/users.service";

@Component({
  selector: "app-collapsible-filter",
  templateUrl: "./collapsible-filter.component.html",
  styleUrls: ["./collapsible-filter.component.scss"],
  standalone: false
})
export class CollapsibleFilterComponent implements OnInit, AfterViewInit {
  @Input() title: string = "";
  @Input() placeholder: string = "";
  @Input() selectedValue: any = "all";
  @Input() selectedOperator: any = null;
  @Input() data: any[] = [];

  @Input() conditionOperators: any[] = [];
  @Input() isDropdown: boolean = false;
  @Input() isDatePicker: boolean = false;
  @Input() isInput: boolean = false;

  @Input() filterKey: string = "";
  @Input() conditionOperatorKey: string = "";

  @Input() isMultiLanguageValue: boolean = false;
  @Input() isMultiple: boolean = false;
  @Input() metaData: any = {};

  @Output() dataSelect = new EventEmitter<any>();
  @Output() sectionToggled = new EventEmitter<void>();

  @Input() isOpen: boolean = false;

  typeData: any[] = [];
  selectedCount = 0;
  datePlaceholder: string = "label-title.select-dates";
  showDatePicker: boolean = false;
  isScheduleDate: boolean = false;
  searchText: string = "";

  @Input() filterDate: any = {};
  activeTab: number = DatePickerTabs.day;

  constructor(public helper: Helper, private usersService: UsersService, private cdr: ChangeDetectorRef) {}

  /**
   * Initializes the component, processes metadata, and sets the selected count.
   * @param void
   * @returns void
   */
  ngOnInit(): void {
    if (this.isMultiple && this.selectedValue.length > 0 && !Array.isArray(this.selectedValue)) {
      this.selectedValue = this.selectedValue.split(",");
    }
    if (this.metaData.activeTab) {
      this.activeTab = this.metaData.activeTab;
    }
    if (this.metaData.filterDate) {
      this.filterDate = this.metaData.filterDate;
    }
    if (this.metaData.activeTab) {
      this.activeTab = this.metaData.activeTab;
    }

    if (this.metaData.isScheduleDate) {
      this.isScheduleDate = this.metaData.isScheduleDate;
    }

    this.setSelectedCount();
  }

  /**
   * Called after the view has been initialized. Sets the selected count.
   * @param void
   * @returns void
   */
  ngAfterViewInit(): void {
    this.setSelectedCount();
  }

  /**
   * Retrieves the display value for a user, considering multi-language support.
   * @param user The user object.
   * @returns The display value for the user.
   */
  getValue(user: any): string {
    if (this.isMultiLanguageValue) {
      return user.name[this.helper.cuurentLang];
    }
    return user.firstName ? `${user.firstName} ${user.lastName}` : `${user.name}`;
  }

  /**
   * Toggles the visibility of the section and emits an event.
   * @param void
   * @returns void
   */
  onToggleSection(): void {
    this.sectionToggled.emit();
  }

  /**
   * Fetches the user list based on the search text and metadata type.
   * Updates the typeData with the fetched users.
   * @param void
   * @returns void
   */
  getUserList(): void {
    this.usersService
      .getUsersList(this.metaData.type, {
        searchText: this.searchText,
        page: 1,
        limit: 100
      })
      .subscribe((response: any) => {
        this.typeData = this.data.filter((item) => this.selectedValue.includes(item.key));
        response.data.users.map((user: any) => {
          this.typeData.push({
            key: user._id,
            value: this.getValue(user)
          });
        });
        setTimeout(() => {
          this.cdr.detectChanges();
        }, 100);
      });
  }

  /**
   * Emits the selected data and filter configuration.
   * @param void
   * @returns void
   */
  emitData(): void {
    const filteredData = [];
    const dataForFilter = this.data;
    if (Array.isArray(this.selectedValue)) {
      filteredData.push(
        ...this.selectedValue.map((item) => {
          const label = dataForFilter.find((data) => data.key === item)?.value;
          if (!label) {
            return false;
          }
          return {
            key: item,
            value: dataForFilter.find((data) => data.key === item)?.value
          };
        })
      );
    } else {
      filteredData.push({
        key: this.selectedValue,
        label: dataForFilter.find((item) => item.key === this.selectedValue)?.value
      });
    }

    this.setSelectedCount();
    this.dataSelect.emit({
      filterData: this.getFilterDataToEmit(),
      filterConfig: {
        title: this.title,
        filterKey: this.filterKey,
        selectedValue: dataForFilter.find((item) => item.key === this.selectedValue)?.value,
        conditionOperatorKey: this.conditionOperatorKey,
        selectedOperator: this.selectedOperator,
        filteredData
      }
    });
  }

  /**
   * Sets the count of selected items based on the selected value.
   * @param void
   * @returns void
   */
  setSelectedCount(): void {
    if (Array.isArray(this.selectedValue)) {
      this.selectedCount = this.selectedValue.length;
    } else if (this.selectedValue !== "all" && this.selectedValue !== "") {
      this.selectedCount = 1;
    } else {
      this.selectedCount = 0;
    }
  }

  /**
   * Toggles the visibility of the date picker.
   * @param void
   * @returns void
   */
  openDatePicker(): void {
    this.showDatePicker = !this.showDatePicker;
  }

  /**
   * Handles changes in the date range selection.
   * Updates the selected value, filter date, and emits the updated filter data.
   * @param data The data containing the formatted date and filter date.
   * @returns void
   */
  onDateRangeChange(data: any): void {
    setTimeout(() => {
      if (data.formattedDate) {
        this.selectedValue = data.formattedDate;
        this.filterDate = data.filterDate;
        this.showDatePicker = false;
        this.activeTab = data.activeTab;
      } else {
        this.showDatePicker = false;
      }
      this.dataSelect.emit({
        filterData: this.getFilterDataToEmit(),
        filterConfig: {
          activeTab: this.activeTab,
          filterDate: this.filterDate,
          title: this.title,
          filterKey: this.filterKey,
          selectedValue: data.formattedDate,
          data: this.data,
          conditionOperatorKey: this.conditionOperatorKey,
          selectedOperator: this.selectedOperator
        }
      });
    }, 100);
  }

  /**
   * Prepares the filter data to be emitted based on the selected value and filter key.
   * @param void
   * @returns The filter data to be emitted.
   */
  getFilterDataToEmit(): any {
    if (this.isDatePicker) {
      return {
        startDate: this.filterDate.from,
        endDate: this.filterDate.to
      };
    }
    return { [this.filterKey]: this.selectedValue };
  }
}
